import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { persistConsent, readConsent } from "./consent";

const ConsentContext = createContext(null);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(readConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const returnFocusRef = useRef(null);

  const closePreferences = useCallback(() => setPreferencesOpen(false), []);
  const restoreFocus = useCallback(() => {
    const trigger = returnFocusRef.current;
    window.requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
  }, []);

  const save = useCallback((analytics) => {
    const next = persistConsent(analytics);
    setConsent(next);
    closePreferences();
    window.__humnAnalyticsConsent?.(analytics);
  }, [closePreferences]);

  const openPreferences = useCallback((trigger) => {
    returnFocusRef.current = trigger instanceof HTMLElement
      ? trigger
      : document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPreferencesOpen(true);
  }, []);

  const value = useMemo(() => ({
    consent,
    preferencesOpen,
    openPreferences,
    closePreferences,
    restoreFocus,
    saveConsent: save,
  }), [closePreferences, consent, openPreferences, preferencesOpen, restoreFocus, save]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) throw new Error("useConsent must be used within ConsentProvider");
  return context;
};